// Shared helpers for rendering batches consistently across the app.
// Used by BatchPicker, BatchChangeDialog, InstructorPayroll Makeup dialog,
// AttendanceManagement chip tooltips, and any future batch-selector surface.

const DAY_ABBR = {
  Monday: "Mon",
  Tuesday: "Tue",
  Wednesday: "Wed",
  Thursday: "Thu",
  Friday: "Fri",
  Saturday: "Sat",
  Sunday: "Sun",
};

/** "18:00" → "6:00 PM"; "09:30" → "9:30 AM"; "" → "". */
export const formatTime12 = (hhmm) => {
  if (!hhmm || typeof hhmm !== "string") return "";
  const [hStr, mStr = "0"] = hhmm.split(":");
  const h = Number(hStr);
  const m = Number(mStr);
  if (Number.isNaN(h)) return hhmm;
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12}:00 ${period}` : `${h12}:${String(m).padStart(2, "0")} ${period}`;
};

/**
 * Groups schedule entries by (start_time, end_time) and returns:
 *   "Mon / Wed / Fri • 6:00 PM–7:00 PM"
 * Multi-band schedules are joined into one line with ", ".
 *
 *   formatBatchSchedule({ schedule: [
 *     {day:"Monday",start_time:"18:00",end_time:"19:00"},
 *     {day:"Wednesday",start_time:"18:00",end_time:"19:00"},
 *     {day:"Saturday",start_time:"10:00",end_time:"11:00"},
 *   ]})
 *   → "Mon / Wed • 6:00 PM–7:00 PM, Sat • 10:00 AM–11:00 AM"
 */
export const formatBatchSchedule = (batch) => {
  const slots = (batch && batch.schedule) || [];
  if (!slots.length) return "No schedule set";

  const groups = new Map();
  for (const s of slots) {
    const key = `${s.start_time || ""}|${s.end_time || ""}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(DAY_ABBR[s.day] || s.day || "—");
  }

  const parts = [];
  for (const [key, days] of groups) {
    const [start, end] = key.split("|");
    const time = start || end ? `${formatTime12(start)}–${formatTime12(end)}` : "";
    parts.push(time ? `${days.join(" / ")} • ${time}` : days.join(" / "));
  }
  return parts.join(", ");
};

/** Resolve instructor display name from either users (linked) or master instructors. */
export const resolveInstructorName = (batch, instructors = [], masterInstructors = []) => {
  if (!batch) return "";
  // Try direct id match against any provided list (user/master). Admins now appear
  // in the instructors list too (Phase-1 unification).
  for (const list of [instructors, masterInstructors]) {
    if (!list) continue;
    const hit = list.find((i) => (i.user_id || i.instructor_id) === batch.instructor_id);
    if (hit?.name) return hit.name;
  }
  return batch.instructor_name || "";
};

/** True if the batch's `end_date` is strictly before today (UTC-naive comparison). */
export const isBatchEnded = (batch) => {
  if (!batch?.end_date) return false;
  try {
    const end = new Date(batch.end_date);
    if (Number.isNaN(end.getTime())) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    return end < today;
  } catch {
    return false;
  }
};
